import {
    FaTimes,
    FaDownload,
    FaChevronLeft,
    FaChevronRight,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { baseURL } from "../../../config/api";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import useAuth from "../../../hooks/useAuth";
import useConfirm from "../../../hooks/useConfirm";
import useDelete from "../../../hooks/useDelete";
import { formatUSPhone, getUSPhoneDigits } from "../../../utils/phoneFormatter";
import Cookies from "js-cookie";
import moment from "moment";
import { combineDateAndTime } from "../../../utils/getUserDateFormat";
import CustomFormatDatePicker from "../../../components/CustomFormatDatePicker";
import InputPicker from "../../../components/InputPicker";

interface UploadedFile {
    id?: number;
    fileName: string;
    type: string;
}

export const EditQuote = () => {
    const token = Cookies.get("token-velox-charter");
    const navigate = useNavigate();
    const idNumber = useLocation();
    const { auth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [respondVia, setRespondVia] = useState("");
    const [departureDate, setDepartureDate] = useState("");
    const [departureTime, setDepartureTime] = useState("");
    const [returnDate, setReturnDate] = useState("");
    const [returnTime, setReturnTime] = useState("");

    const [departureCity, setDepartureCity] = useState("");
    const [departureState, setDepartureState] = useState("");

    const [destinationCity, setDestinationCity] = useState("");
    const [destinationState, setDestinationState] = useState("");
    const [tripType, setTripType] = useState("");

    const [groupTypes, setGroupTypes] = useState<any[]>([]);
    const [groupTypeId, setGroupTypeId] = useState<number | "">("");

    const [passengers, setPassengers] = useState("");

    const [additionalInfo, setAdditionalInfo] = useState("");

    const [createdAt, setCreatedAt] = useState("");
    const [createdBy, setCreatedBy] = useState("");
    const [updatedAt, setUpdatedAt] = useState("");
    const [updatedBy, setUpdatedBy] = useState("");


    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isPhoneValid, setIsPhoneValid] = useState(true);

    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [previewIndex, setPreviewIndex] =
        useState<number | null>(null);

    const departureTimeRef = useRef<HTMLInputElement>(null);
    const returnTimeRef = useRef<HTMLInputElement>(null);

    const previewContainerRef = useRef<HTMLDivElement>(null);

    const previewFile =
        previewIndex !== null
            ? uploadedFiles[previewIndex]
            : null;
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);

    const isImage =
        previewFile?.type?.startsWith("image");

    const isPdf =
        previewFile?.type === "application/pdf";

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (email.trim() !== "") {
            timer = setTimeout(() => {
                const valid =
                    /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email.trim());

                setIsEmailValid(valid);
            }, 800);
        } else {
            setIsEmailValid(true);
        }

        return () => clearTimeout(timer);
    }, [email]);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (phone.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(phone);
                setIsPhoneValid(valid);
            }, 500);
        } else {
            setIsPhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [phone]);

    const handleDownload = async () => {
        if (!previewFile) return;

        try {
            const response = await fetch(
                `${baseURL}common-files/get/${previewFile.fileName}`
            );

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = url;
            link.download = previewFile.fileName;

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    const showPrevious = () => {
        if (previewIndex === null) return;

        setPreviewIndex(
            previewIndex === 0
                ? uploadedFiles.length - 1
                : previewIndex - 1
        );
    };

    const showNext = () => {
        if (previewIndex === null) return;

        setPreviewIndex(
            previewIndex === uploadedFiles.length - 1
                ? 0
                : previewIndex + 1
        );
    };

    useEffect(() => {
        if (previewFile) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [previewFile]);

    useEffect(() => {
        if (!previewFile) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case "Escape":
                    setPreviewIndex(null);
                    break;

                case "ArrowLeft":
                    showPrevious();
                    break;

                case "ArrowRight":
                    showNext();
                    break;

                default:
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewFile, previewIndex]);

    const handlePhoneChangeLocal = (value: string) => {
        const input = document.activeElement as HTMLInputElement;

        if (!input || input.tagName !== "INPUT") {
            setPhone(getUSPhoneDigits(value));
            return;
        }

        const cursor = input.selectionStart || 0;

        const digitsBeforeCursor = value
            .slice(0, cursor)
            .replace(/\D/g, "").length;

        const digits = getUSPhoneDigits(value);

        setPhone(digits);

        requestAnimationFrame(() => {
            const formatted = formatUSPhone(digits);

            let newCursor = formatted.length;
            let digitCount = 0;

            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) digitCount++;

                if (digitCount >= digitsBeforeCursor) {
                    newCursor = i + 1;
                    break;
                }
            }

            try {
                input.setSelectionRange(newCursor, newCursor);
            } catch (error) {
                console.error("Failed to set cursor position:", error);
            }
        });
    };

    useEffect(() => {
        axios
            .get(`${baseURL}group-types/`)
            .then((response) => {
                setGroupTypes(response?.data?.data || []);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    const formatDateTime = (date: string, time: string) => {
        if (!date) return "";

        const formattedDate = moment(date).format("MM/DD/YYYY");

        if (!time) return formattedDate;

        const formattedTime = moment(time, "HH:mm").format("hh:mm A");

        return `${formattedDate} ${formattedTime}`;
    };


    const parseDateTime = (value?: string | null) => {
        if (!value) {
            return {
                date: "",
                time: "",
            };
        }

        const m = moment(value);

        if (!m.isValid()) {
            return {
                date: "",
                time: "",
            };
        }

        return {
            date: m.format("YYYY-MM-DD"),
            time: m.format("HH:mm"),
        };
    }

    useEffect(() => {
        if (idNumber?.state) {
            setPleaseWaitPopup(true);

            axios
                .get(`${baseURL}quotes/${idNumber?.state}`)
                .then((response) => {
                    const quote = response?.data?.data;

                    setFirstName(quote?.firstName || "");
                    setLastName(quote?.lastName || "");
                    setEmail(quote?.email || "");
                    setPhone(quote?.phone || "");
                    setCompanyName(quote?.company || "");
                    setRespondVia(quote?.respondVia || "");
                    const departure = parseDateTime(quote?.departureDate);
                    const returnTrip = parseDateTime(quote?.returnDate);

                    setDepartureDate(departure.date);
                    setDepartureTime(departure.time);

                    setReturnDate(returnTrip.date);
                    setReturnTime(returnTrip.time);
                    setDepartureCity(quote?.departureCity || "");
                    setDepartureState(quote?.departureState || "");
                    setDestinationCity(quote?.destinationCity || "");
                    setDestinationState(quote?.destinationState || "");
                    setTripType(quote?.tripType || "");
                    setGroupTypeId(quote?.groupTypeId || "");
                    setPassengers(
                        quote?.numberOfPassengers?.toString() || ""
                    );
                    setAdditionalInfo(quote?.additionalTripInformation || "");
                    setCreatedAt(quote?.createdAt || "");
                    setCreatedBy(quote?.createdBy || "");
                    setUpdatedAt(quote?.updatedAt || "");
                    setUpdatedBy(quote?.updatedBy || "");

                    axios
                        .get(
                            `${baseURL}file-upload/attribute/${quote.id}/Quote`
                        )
                        .then((res) => {

                            const files =
                                res?.data?.data?.map((item: any) => ({

                                    id: item.id,

                                    fileName: item.fileName,

                                    type:
                                        item.fileName
                                            .split(".")
                                            .pop()
                                            ?.toLowerCase() === "pdf"
                                            ? "application/pdf"
                                            : item.fileName.match(
                                                /\.(jpg|jpeg|png|gif|webp)$/i
                                            )
                                                ? "image/*"
                                                : "application/octet-stream",

                                })) || [];

                            setUploadedFiles(files);

                            setPleaseWaitPopup(false);

                        })
                        .catch(() => {
                            setPleaseWaitPopup(false);
                        });
                    setPleaseWaitPopup(false);
                })
                .catch((error) => {
                    console.error(error);
                    setPleaseWaitPopup(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);

    const requiresReturnDate = tripType === "Round Trip";

    const isFormValid =
        firstName.trim() !== "" &&
        lastName.trim() !== "" &&
        email.trim() !== "" &&
        phone.trim() !== "" &&
        respondVia.trim() !== "" &&
        departureDate !== "" &&
        departureTime.trim() !== "" &&
        departureCity.trim() !== "" &&
        departureState.trim() !== "" &&
        destinationCity.trim() !== "" &&
        destinationState.trim() !== "" &&
        tripType.trim() !== "" &&
        passengers !== "" &&
        (!requiresReturnDate || (returnDate.trim() !== "" && returnTime.trim() !== "")) &&
        isEmailValid &&
        isPhoneValid;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isFormValid) return;

        setPleaseWaitPopup(true);

        await axios
            .put(
                `${baseURL}quotes/${idNumber.state}`,
                JSON.stringify({
                    firstName,
                    lastName,
                    email,
                    phone,
                    company: companyName,
                    respondVia,
                    departureDate: combineDateAndTime(
                        departureDate,
                        departureTime
                    ),
                    returnDate: returnDate
                        ? combineDateAndTime(
                            returnDate,
                            returnTime
                        )
                        : null,
                    departureCity,
                    departureState,
                    destinationCity,
                    destinationState,
                    tripType,
                    groupTypeId: groupTypeId || 0,
                    numberOfPassengers: Number(passengers),
                    additionalTripInformation: additionalInfo,
                    statusId: 1,

                    updatedAt: new Date().toLocaleString(),
                    updatedBy: auth?.givenname
                }),
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: false,
                }
            )
            .then(() => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                console.error("Error:", error);
            });
    }

    const deletingQuote = async () => {
        setDeleteData({
            id: idNumber?.state,
            title: `Delete User`,
            summary: `Are you sure you want to delete this quote?`,
            isDelete: (callback: any) => {
                setPleaseWaitPopup(true);
                callback();

                axios.delete(`${baseURL}quotes/${idNumber?.state}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                    .then(() => {
                        setPleaseWaitPopup(false);
                        navigate(-1);
                    })
                    .catch((error) => {
                        setConfirmData({
                            title: "Warning",
                            summary: error.response?.data || "Delete failed.",
                            confirmBtn: "Ok",
                            isConfirm: (cb: any) => cb(),
                        });

                        setConfirmPopup(true);
                        setPleaseWaitPopup(false);
                    });
            },
        });

        setDeletePopup(true);
    }

    const ViewField = ({
        label,
        value,
    }: {
        label: string;
        value: React.ReactNode;
    }) => (
        <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
                {label}
            </label>

            <div className="min-h-[48px] flex items-center text-gray-900">
                {value || "-"}
            </div>
        </div>
    );

    return (
        <>
            <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
                <div className="w-full bg-white p-4 rounded-md">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-semibold">
                            Quote Details
                        </h2>

                        {!isEditing && (
                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="px-5 py-2 rounded-md bg-primary text-white"
                            >
                                Edit
                            </button>
                        )}
                    </div>

                    <div className="mt-2 p-2">
                        <form onSubmit={handleSubmit}>

                            <div className="mb-10">
                                <h2 className="text-lg font-semibold text-primary mb-6">
                                    Contact Information
                                </h2>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                First Name <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                placeholder="Enter first name"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        ) : (
                                            <ViewField
                                                label="First Name"
                                                value={firstName}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Last Name <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                placeholder="Enter last name"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        ) : (
                                            <ViewField
                                                label="Last Name"
                                                value={lastName}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <>
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="Enter email address"
                                                    className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                    required
                                                />

                                                {!isEmailValid && (
                                                    <p className="text-xs text-red-500 mt-1">
                                                        Please enter a valid email address
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <ViewField
                                                label="Email Address"
                                                value={email}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Phone Number <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <>
                                                <input
                                                    type="text"
                                                    value={formatUSPhone(phone)}
                                                    onChange={(e) =>
                                                        handlePhoneChangeLocal(e.target.value)
                                                    }
                                                    placeholder="(281) 371-9009"
                                                    className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                    required
                                                />

                                                {!isPhoneValid && (
                                                    <p className="text-xs text-red-500 mt-1">
                                                        Please enter a valid 10-digit phone number
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <ViewField
                                                label="Phone Number"
                                                value={formatUSPhone(phone)}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Company Name
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                                placeholder="Enter company name (optional)"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            />
                                        ) : (
                                            <ViewField
                                                label="Company Name"
                                                value={companyName}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Respond Via <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <select
                                                value={respondVia}
                                                onChange={(e) => setRespondVia(e.target.value)}
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            >
                                                <option value="">Select Contact Method</option>
                                                <option value="Phone">Phone</option>
                                                <option value="Email">Email</option>
                                            </select>
                                        ) : (
                                            <ViewField
                                                label="Respond Via"
                                                value={respondVia}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-10">
                                <h2 className="text-lg font-semibold text-primary mb-6">
                                    Trip Details
                                </h2>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div className="md:col-span-2">
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Trip Type <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <div className="flex flex-wrap gap-2 h-12 items-center">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="Round Trip"
                                                        checked={tripType === "Round Trip"}
                                                        onChange={(e) => setTripType(e.target.value)}
                                                    />
                                                    <span>Round Trip</span>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="One Way"
                                                        checked={tripType === "One Way"}
                                                        onChange={(e) => {
                                                            setTripType(e.target.value);
                                                            setReturnDate("");
                                                        }}
                                                    />
                                                    <span>One Way</span>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="Sameday Return"
                                                        checked={tripType === "Sameday Return"}
                                                        onChange={(e) => setTripType(e.target.value)}
                                                    />
                                                    <span>Same Day Return</span>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="tripType"
                                                        value="Overnight"
                                                        checked={tripType === "Overnight"}
                                                        onChange={(e) => setTripType(e.target.value)}
                                                    />
                                                    <span>Overnight</span>
                                                </label>
                                            </div>
                                        ) : (
                                            <ViewField
                                                label="Trip Type"
                                                value={tripType}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Departure Date <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <div className="flex flex-col gap-2 md:flex-row">
                                                <div className="relative w-full md:w-1/2">
                                                    <CustomFormatDatePicker
                                                        maxDate="9999-12-31T23:59"
                                                        value={departureDate}
                                                        onChange={(value) => {
                                                            setDepartureDate(value);
                                                        }}
                                                        required={true}
                                                    />
                                                </div>

                                                <div className="w-full md:w-1/2">
                                                    <InputPicker
                                                        type="time"
                                                        inputRef={departureTimeRef}
                                                        inputValue={departureTime}
                                                        setInputValue={(inputValue) => setDepartureTime(inputValue)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <ViewField
                                                label="Departure Date"
                                                value={formatDateTime(departureDate, departureTime)}
                                            />

                                        )}
                                    </div>

                                    {tripType !== "One Way" && (
                                        <div>
                                            {isEditing && (
                                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                                    Return Date{" "}
                                                    {requiresReturnDate && (
                                                        <span className="text-red-500">*</span>
                                                    )}
                                                </label>
                                            )}

                                            {isEditing ? (
                                                <div className="flex flex-col gap-2 md:flex-row">
                                                    <div className="relative w-full md:w-1/2">
                                                        <CustomFormatDatePicker
                                                            maxDate="9999-12-31T23:59"
                                                            value={returnDate}
                                                            onChange={(value) => {
                                                                setReturnDate(value);
                                                            }}
                                                            required={requiresReturnDate}
                                                        />
                                                    </div>

                                                    <div className="w-full md:w-1/2">
                                                        <InputPicker
                                                            type="time"
                                                            inputRef={returnTimeRef}
                                                            inputValue={returnTime}
                                                            setInputValue={(inputValue) => setReturnTime(inputValue)}
                                                            required={requiresReturnDate}
                                                        />
                                                    </div>
                                                </div>

                                            ) : (
                                                <ViewField
                                                    label="Return Date"
                                                    value={formatDateTime(returnDate, returnTime)}
                                                />
                                            )}
                                        </div>
                                    )}

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Departure City <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={departureCity}
                                                onChange={(e) => setDepartureCity(e.target.value)}
                                                placeholder="Enter departure city"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        ) : (
                                            <ViewField
                                                label="Departure City"
                                                value={departureCity}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Departure State <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={departureState}
                                                onChange={(e) => setDepartureState(e.target.value)}
                                                placeholder="Enter departure state"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        ) : (
                                            <ViewField
                                                label="Departure State"
                                                value={departureState}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Destination City <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={destinationCity}
                                                onChange={(e) => setDestinationCity(e.target.value)}
                                                placeholder="Enter destination city"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        ) : (
                                            <ViewField
                                                label="Destination City"
                                                value={destinationCity}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Destination State <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={destinationState}
                                                onChange={(e) => setDestinationState(e.target.value)}
                                                placeholder="Enter destination state"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        ) : (
                                            <ViewField
                                                label="Destination State"
                                                value={destinationState}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">
                                            Itinerary Files
                                        </label>

                                        {uploadedFiles.length > 0 ? (
                                            <div className="mt-4 flex flex-wrap gap-3">
                                                {uploadedFiles.map((file, index) => (
                                                    <div
                                                        key={index}
                                                        className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-300 shadow-sm hover:shadow-md transition cursor-pointer"
                                                    >
                                                        {file.type.startsWith("image") ? (
                                                            <img
                                                                alt="Itinerary"
                                                                src={`${baseURL}common-files/get/${file.fileName}`}
                                                                className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                                                                onClick={() => setPreviewIndex(index)}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="flex h-full items-center justify-center text-3xl"
                                                                onClick={() => setPreviewIndex(index)}
                                                            >
                                                                📄
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="mt-4 text-sm text-gray-500">
                                                No itinerary files uploaded.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-10">
                                <h2 className="text-lg font-semibold text-primary mb-6">
                                    Group Details
                                </h2>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Group Type <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <select
                                                value={groupTypeId}
                                                onChange={(e) => setGroupTypeId(Number(e.target.value))}
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            >
                                                <option value="">Select Group Type</option>

                                                {groupTypes.map((item: any) => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <ViewField
                                                label="Group Type"
                                                value={
                                                    groupTypes.find((item: any) => item.id === groupTypeId)?.name
                                                }
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {isEditing && (
                                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                                Passengers <span className="text-red-500">*</span>
                                            </label>
                                        )}

                                        {isEditing ? (
                                            <input
                                                type="number"
                                                min={1}
                                                value={passengers}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    if (/^\d*$/.test(value)) {
                                                        setPassengers(value);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (["-", "+", ".", "e", "E"].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const pastedText = e.clipboardData.getData("text");

                                                    if (!/^\d+$/.test(pastedText)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onWheel={(e) => e.currentTarget.blur()}
                                                placeholder="Enter number of passengers"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                required
                                            />
                                        ) : (
                                            <ViewField
                                                label="Passengers"
                                                value={passengers}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div>
                                    {isEditing && (
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Additional Information
                                        </label>
                                    )}

                                    {isEditing ? (
                                        <textarea
                                            rows={6}
                                            value={additionalInfo}
                                            onChange={(e) => setAdditionalInfo(e.target.value)}
                                            placeholder="Provide any additional information about your trip..."
                                            className="w-full p-4 border border-gray-300 rounded-md bg-white resize-none outline-none focus:border-primary"
                                        />
                                    ) : (
                                        <ViewField
                                            label="Additional Information"
                                            value={additionalInfo}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="mt-10">
                                <h2 className="text-lg font-semibold text-primary mb-6">
                                    Audit Information
                                </h2>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <ViewField
                                        label="Created At"
                                        value={
                                            createdAt
                                                ? moment(createdAt).format("MM/DD/YYYY hh:mm A")
                                                : "-"
                                        }
                                    />

                                    <ViewField
                                        label="Created By"
                                        value={createdBy}
                                    />

                                    <ViewField
                                        label="Updated At"
                                        value={
                                            updatedAt
                                                ? moment(updatedAt).format("MM/DD/YYYY hh:mm A")
                                                : "-"
                                        }
                                    />

                                    <ViewField
                                        label="Updated By"
                                        value={updatedBy}
                                    />
                                </div>
                            </div>


                            {isEditing && (
                                <div className="flex gap-4 mt-8">
                                    <button
                                        type="submit"
                                        className="px-4 py-2 rounded bg-primary text-white"
                                    >
                                        Save
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditing(false);
                                            navigate(-1);
                                        }}
                                        className="px-4 py-2 rounded border border-primary text-primary"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        onClick={deletingQuote}
                                        className="px-4 py-2 rounded bg-red-500 text-white"
                                    >
                                        Delete
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div >
            </div >


            {previewFile && (
                <div
                    ref={previewContainerRef}
                    onClick={() => setPreviewIndex(null)}
                    className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex flex-col transition-opacity duration-300"
                >
                    <div className="h-16 flex items-center justify-between px-6 border-b border-white/10">

                        <div className="text-white flex items-center gap-3">
                            <span className="font-bold truncate max-w-[350px]">
                                {previewFile.fileName}
                            </span>

                            <span className="text-white/60">
                                {previewIndex! + 1} / {uploadedFiles.length}
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload();
                                }}
                                title="Download"
                                className="w-10 h-10 rounded-lg hover:bg-blue-600 transition text-white flex items-center justify-center"
                            >
                                <FaDownload />
                            </button>

                            <button
                                onClick={() => setPreviewIndex(null)}
                                className="w-10 h-10 rounded-lg hover:bg-red-500 transition text-white flex items-center justify-center"
                            >
                                <FaTimes />
                            </button>

                        </div>

                    </div>

                    <div className="flex-1 flex items-center justify-center relative overflow-hidden">

                        {uploadedFiles.length > 1 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showPrevious();
                                }}
                                className="absolute left-6 z-20 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition"
                            >
                                <FaChevronLeft size={22} />
                            </button>
                        )}

                        <div
                            onClick={(e) => e.stopPropagation()}
                            className="
max-w-[90vw]
max-h-[80vh]
flex
items-center
justify-center
animate-[zoomIn_.25s_ease]
"
                        >
                            {isImage ? (
                                <img
                                    src={`${baseURL}common-files/get/${previewFile.fileName}`}
                                    alt=""
                                    className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
                                />
                            ) : isPdf ? (
                                <iframe
                                    src={`${baseURL}common-files/get/${previewFile.fileName}`}
                                    title="Preview"
                                    className="w-[85vw] h-[82vh] bg-white rounded-xl"
                                />
                            ) : (
                                <div className="bg-white rounded-xl p-10 flex flex-col items-center gap-5 shadow-2xl">

                                    <div className="text-8xl">
                                        📄
                                    </div>

                                    <h3 className="text-xl font-semibold">
                                        Preview isn't available
                                    </h3>

                                    <p className="text-gray-500 text-center">
                                        This file type cannot be previewed in the browser.
                                    </p>

                                    <div className="flex gap-3">

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownload();
                                            }}
                                            className="px-5 py-2 rounded-lg bg-primary text-white"
                                        >
                                            Download
                                        </button>

                                        <a
                                            href={`${baseURL}common-files/get/${previewFile.fileName}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-5 py-2 rounded-lg border"
                                        >
                                            Open
                                        </a>

                                    </div>

                                </div>
                            )}
                        </div>

                        {uploadedFiles.length > 1 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showNext();
                                }}
                                className="absolute right-6 z-20 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition"
                            >
                                <FaChevronRight size={22} />
                            </button>
                        )}

                    </div>

                </div>
            )}

            <PleasewaitPopup showPopup={pleaseWaitPopup} />
        </>
    )
}